import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh.js';
import { BufferGeometry, DoubleSide, Group, Mesh, MeshStandardMaterial, Vector3 } from 'three';
import { gumMaterial } from '@modules/three/common/materials/jawMaterials.ts';
import ToothExport from '@export/three/ToothExport.ts';
import type { ToothCorrectionResponse } from '@/schema/toothCorrection';
import type { ToothSegmentationResponse } from '@/schema/toothSegmentation';
import { upperFDI, lowerFDI } from '@/common/FDI_Notation';
import InteractiveSpline from '@/modules/three/common/curve/interactiveSpline/InteractiveSpline.ts';

export default class JawExport extends HalfEdgeMesh {
  public readonly teeth: ToothExport[] = [];
  private readonly toothByFdi = new Map<number, ToothExport>();
  private readonly jawType: 'upper' | 'lower';
  public trimCurve: InteractiveSpline | null = null;
  private gumBoundaryIndices: number[] = [];
  private trimCurvePoints: Vector3[] = [];
  private cappingDebugGroup: Group | null = null;

  constructor(
    gumGeometry: BufferGeometry,
    teethGeometries: BufferGeometry[],
    teethData: ToothSegmentationResponse[],
    jawType: 'upper' | 'lower',
  ) {
    super(gumGeometry);
    this.material = gumMaterial;
    this.geometry.computeVertexNormals();
    this.jawType = jawType;
    this.initializeTeeth(teethGeometries, teethData);
    this.updateTrimCurve();
  }

  public updateGumGeometry(geometry: BufferGeometry) {
    this.setNewGeometry(geometry);
    this.geometry.attributes.position.needsUpdate = true;
    this.updateTrimCurve();
  }

  public setGumBoundaryIndices(indices: number[]) {
    this.gumBoundaryIndices = indices;
    this.updateTrimCurve();
  }

  public updateTrimCurve() {
    let boundaryVertices: Vector3[] = [];

    if (this.gumBoundaryIndices.length > 0) {
      const indexSet = new Set(this.gumBoundaryIndices);
      this.halfEdges.vertices.forEach(v => {
        if (indexSet.has(v.renderId)) {
          boundaryVertices.push(v.position.clone());
        }
      });
    } else {
      boundaryVertices = this.getBorderLoops().flatMap((loop) => loop.map((vertex) => vertex.position.clone()));
    }

    if (this.trimCurve) {
      this.remove(this.trimCurve);
      this.trimCurve.dispose();
      this.trimCurve = null;
    }

    this.trimCurvePoints = [];

    if (boundaryVertices.length > 0) {
      this.trimCurve = new InteractiveSpline(boundaryVertices, this, { targetControlPoints: 175 });
      this.add(this.trimCurve);
      this.trimCurve.setVisible(true);
      this.trimCurvePoints = this.trimCurve.getControlPoints();
    }
  }

  public getTrimCurvePoints() {
    return this.trimCurvePoints.map((point) => point.clone());
  }

  private initializeTeeth(teethGeometries: BufferGeometry[], teethData: ToothSegmentationResponse[]) {
    teethGeometries.forEach((geometry, index) => {
      const data = teethData[index];
      if (!geometry || !data) {
        return;
      }

      const normalizedFdi = this.normalizeFdi(index, data.fdi);
      const tooth = new ToothExport(geometry, { ...data, fdi: normalizedFdi });
      this.teeth.push(tooth);
      this.toothByFdi.set(normalizedFdi, tooth);
    });

    this.rootState.scene.updateMatrixWorld(true);
    for (const tooth of this.teeth) {
      tooth.buildCap(this, { quiet: true });
    }
  }

  /**
   * Lightweight Mesh instances wrapping each tooth's cached capped geometry, with the
   * tooth's current world transform baked into `matrixWorld`. The downstream V7
   * generator clones geometry + matrixWorld so these meshes don't enter the scene graph.
   */
  public getCappedTeethMeshes(): Mesh[] {
    this.updateMatrixWorld(true);
    const result: Mesh[] = [];
    for (const tooth of this.teeth) {
      const capped = tooth.getCappedGeometry();
      if (!capped) continue;
      tooth.updateMatrixWorld(true);
      const mesh = new Mesh(capped);
      mesh.matrixAutoUpdate = false;
      mesh.matrix.copy(tooth.matrix);
      mesh.matrixWorld.copy(tooth.matrixWorld);
      result.push(mesh);
    }
    return result;
  }

  /**
   * Lightweight Mesh instances wrapping each tooth's *uncapped* geometry (the raw
   * crown / real tooth surfaces, open at the root — no synthetic gum cap), with the
   * tooth's current world transform baked into `matrixWorld`. V7 measures the shell
   * offset distance against these so the band follows the real tooth rather than the
   * cap. The downstream generator clones the geometry, so the tooth's own geometry is
   * never mutated.
   */
  public getUncappedTeethMeshes(): Mesh[] {
    this.updateMatrixWorld(true);
    const result: Mesh[] = [];
    for (const tooth of this.teeth) {
      if (!tooth.geometry) continue;
      tooth.updateMatrixWorld(true);
      const mesh = new Mesh(tooth.geometry);
      mesh.matrixAutoUpdate = false;
      mesh.matrix.copy(tooth.matrix);
      mesh.matrixWorld.copy(tooth.matrixWorld);
      result.push(mesh);
    }
    return result;
  }

  /**
   * Toggle a translucent debug overlay of the capped tooth meshes so users can
   * visually verify the seal against the gum. Overlay meshes follow each tooth's
   * world transform via `matrixAutoUpdate = false` updated from `tooth.matrixWorld`.
   */
  public setCappingDebugVisible(visible: boolean): void {
    if (!visible) {
      this.disposeCappingDebug();
      return;
    }

    if (this.cappingDebugGroup) {
      this.disposeCappingDebugChildren();
    } else {
      this.cappingDebugGroup = new Group();
      this.cappingDebugGroup.name = `CappingDebug-${this.jawType}`;
      this.rootState.scene.add(this.cappingDebugGroup);
    }

    const material = new MeshStandardMaterial({
      color: 0x66ddff,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      side: DoubleSide,
    });

    for (const tooth of this.teeth) {
      const capped = tooth.getCappedGeometry();
      if (!capped) continue;
      tooth.updateMatrixWorld(true);
      const mesh = new Mesh(capped, material);
      mesh.matrixAutoUpdate = false;
      mesh.matrix.copy(tooth.matrix);
      mesh.matrixWorld.copy(tooth.matrixWorld);
      mesh.renderOrder = 50;
      this.cappingDebugGroup.add(mesh);
    }
  }

  private disposeCappingDebugChildren() {
    if (!this.cappingDebugGroup) return;
    this.cappingDebugGroup.children.forEach((child) => {
      if (child instanceof Mesh) {
        const mat = child.material;
        if (Array.isArray(mat)) {
          mat.forEach((m) => m.dispose());
        } else {
          mat.dispose();
        }
      }
    });
    this.cappingDebugGroup.clear();
  }

  private disposeCappingDebug() {
    if (!this.cappingDebugGroup) return;
    this.disposeCappingDebugChildren();
    this.cappingDebugGroup.removeFromParent();
    this.cappingDebugGroup = null;
  }

  private normalizeFdi(index: number, fallback: number) {
    const lookup = this.jawType === 'upper' ? upperFDI : lowerFDI;
    return lookup[index] ?? fallback;
  }

  public resetTeeth() {
    this.teeth.forEach((tooth) => tooth.resetTransforms());
  }

  public applyToothCorrections(corrections: ToothCorrectionResponse[]) {
    this.resetTeeth();
    corrections.forEach((correction) => {
      const tooth = this.toothByFdi.get(correction.fdi);
      console.log('applyToothCorrections', correction.fdi, tooth);
      if (!tooth) return;
      tooth.applyCorrection(correction.movement, correction.rotation);
    });
    this.rootState.scene.updateMatrixWorld(true);
  }

  public dispose() {
    this.teeth.forEach((tooth) => tooth.dispose());
    this.disposeCappingDebug();
    this.removeFromParent();
    this.geometry.dispose();
  }

  public getJawType() {
    return this.jawType;
  }
}
